import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {ColumnType, DotNestedKeys} from '@shared/helper/reflections';
import {strings} from '@shared/helper/strings';
import {chunk, get} from 'lodash-es';
import startCase from 'lodash-es/startCase';
import {ClipboardService} from 'ngx-clipboard';
import {MessageService} from 'primeng/api';
import {TagComponentValue} from '@shared/components/tag/tag.component';
import {COLOR} from '@app/@core/colors';

export type ModelViewerFields<T> = ObjectField<T>[];

interface EnumElement {
    value: string;
    color: COLOR;
}

export interface ObjectField<T = any> {
    key: DotNestedKeys<T> | '';
    title?: string;
    type?: ColumnType | 'enum';
    click?: ModelViewerClickFn<T>;
    defaultValue?: string;
    class?: string;
    func?: (value, row: T) => string| string[] | EnumElement;
}

type ModelViewerClickFn<T> = (a: T) => void;

interface KV<T> {
    key: string;
    label: string;
    value: any;
    action: ModelViewerClickFn<T>;
    model: T;
    class: string;
    // type: ColumnType | 'enum';
    type: ColumnType;
}

export const objectField = {
  sm: function sm<T>(columns: ObjectField<T>[], includeHeader = true): ObjectField<T>[] {
    return columns.map(column => ({
        ...column,
        class: 'text-sm',
    }));
  }
};

@Component({
    template: `
        <div class="flex">
            <table class="ngx-table mr-2">
                <tr *ngFor="let kvArray of chunks">
                    <ng-container *ngFor="let kv of kvArray">
                        <td *ngIf="kv.label" align="right"
                            class="{{leftColumnStyleClass}} {{textSize}}">{{ kv.label }}:
                        </td>

                        <ng-container *ngIf="kv.action;else noActionClickTpl">
                            <td>
                                <div *ngIf="kv.key === 'id'"
                                     class="flex">
                                    <button pButton
                                            icon="pi pi-copy"
                                            class="p-button-link"
                                            pTooltip="Copy ID"
                                            (click)="copyIdToClipboard()"></button>
                                    <button pButton
                                            icon="pi pi-external-link"
                                            class="p-button-link"
                                            pTooltip="View Detail"
                                            (click)="onClick(kv)"></button>
                                </div>
                                <button *ngIf="kv.key !== 'id'"
                                        pButton
                                        type="button"
                                        [label]="kv.value"
                                        class="p-button-link"
                                        (click)="onClick(kv)"></button>
                            </td>
                        </ng-container>
                        <ng-template #noActionClickTpl>
                            <td [class]="textSize" [colSpan]="kv.label ? 1 : 2">
                                <ng-container *ngIf="kv.type === 'enum'">
                                    <p-tag styleClass="bg-{{kv.value.color}}-500" [value]="kv.value.value"></p-tag>
                                </ng-container>
                                <ng-container *ngIf="kv.type !== 'enum'">
                                    <strong class="{{kv.class}}">{{ kv.value }}</strong>
                                </ng-container>
                            </td>
                        </ng-template>

                    </ng-container>
                </tr>
            </table>

            <div *ngIf="header"
                 class="flex flex-column">
                <!--            <h5>{{header}}</h5>-->
                <button pButton
                        icon="pi pi-external-link"
                        class="p-button-link mt-2"
                        pTooltip="View Detail"
                        (click)="onClickHeader()">
                </button>
                <button pButton
                        icon="pi pi-copy"
                        class="p-button-link"
                        pTooltip="Copy ID"
                        (click)="copyIdToClipboard()">
                </button>
            </div>
        </div>

    `,
    selector: 'app-model-viewer',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModelViewerComponent<T> implements OnChanges {
    @Input() fields: ObjectField<T>[] = [];
    @Input() model: T;
    @Input() cols = 1;
    @Input() header: string;
    @Input() size: '' | 'xs' | 'sm' = '';
    @Input() leftColumnStyleClass = 'w-3';

    @Output() clickHeader = new EventEmitter<T>();

    chunks: KV<T>[][];
    textSize = '';

    constructor(private clipboardService: ClipboardService, private messageService: MessageService) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.model) {
            const rows = this.fields.map(field => {
                let rawValue;
                if (field.func){
                    rawValue = field.func(field.key ? this.model[field.key] : null, this.model);
                }else {
                    rawValue = field.key ? get(this.model, field.key) : null;
                }

                let value;
                if (field.type === 'enum'){
                    value = rawValue;
                } else {
                    value = strings.transform(rawValue, field.type, field.defaultValue);
                }

                return {
                    label: field.title ?? startCase(field.key),
                    value,
                    action: field.click,
                    key: field.key as string,
                    class: field.class,
                    type: field.type
                } as KV<T>;
            });

            this.chunks = chunk(rows, this.cols);
        }
        if (changes.size) {
            if (this.size === 'xs') {
                this.textSize = 'text-xs';
            } else if (this.size === 'sm') {
                this.textSize = 'text-sm';
            }
        }
    }

    onClickHeader() {
        if (this.clickHeader) {
            this.clickHeader.emit(this.model);
        }
    }

    onClick(kv: KV<T>) {
        if (kv.action) {
            kv.action(kv.model);
        }
    }

    copyIdToClipboard() {
        const modelId = this.model['id'];
        this.clipboardService.copy(modelId);
        this.messageService.add({severity: 'success', summary: 'Copy ID to clipboard', detail: modelId});
    }
}
